import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { SOCIALS, SocialRow } from "../Socials";
import { QuillFeather } from "../Logo";
import { ADDRESSES, EXPLORER_TOKEN, EXPLORER_ADDR, dexSwapUrl } from "../../lib/config";

export default function Footer() {
    return (
        <footer className="mt-32 border-t border-white/[0.06]">
            <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
                <div>
                    <div className="mb-2 inline-flex items-center gap-2">
                        <QuillFeather size={22} tone="lime" className="-mt-1" />
                        <span className="display text-2xl font-black gradient-text">Safe Piggy</span>
                    </div>
                    <p className="text-piggy-cream/50 text-sm leading-relaxed max-w-xs">
                        Six earning paths, one merry band. The deflationary tax token of Robinhood Chain.
                    </p>
                </div>
                <FooterCol
                    title="Protocol"
                    items={[
                        { label: "Swap",      to: "/swap" },
                        { label: "Mint",      to: "/mint" },
                        { label: "Stake",     to: "/stake" },
                        { label: "LP",        to: "/lp" },
                        { label: "Referrals", to: "/referrals" },
                        { label: "Hall",      to: "/hall" },
                    ]}
                />
                <FooterCol
                    title="Resources"
                    items={[
                        // Contract addresses are listed in the Hall page's contracts panel.
                        { label: "Contract addresses", to: "/hall" },
                        ...(ADDRESSES.PIGGY_TOKEN
                            ? [{ label: "PIGGY on Blockscout", href: `${EXPLORER_TOKEN}/${ADDRESSES.PIGGY_TOKEN}` }]
                            : []),
                        ...(ADDRESSES.PIGGY_PAIR
                            ? [{ label: "PIGGY/WETH pool", href: `${EXPLORER_ADDR}/${ADDRESSES.PIGGY_PAIR}` }]
                            : []),
                        ...(dexSwapUrl(ADDRESSES.PIGGY_TOKEN)
                            ? [{ label: "Trade on Uniswap", href: dexSwapUrl(ADDRESSES.PIGGY_TOKEN)! }]
                            : []),
                    ]}
                />
                <div>
                    <div className="text-piggy-cream/40 text-xs font-semibold uppercase tracking-wider mb-3">Community</div>
                    <div className="mb-4">
                        <SocialRow size="md" />
                    </div>
                    <a
                        href={SOCIALS.email}
                        className="text-xs text-piggy-cream/50 hover:text-piggy-cream transition-colors break-all"
                    >
                        support@safepiggy.xyz
                    </a>
                </div>
            </div>

            <div className="border-t border-white/[0.04]">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div id="disclaimer" className="glass-pink rounded-2xl p-5 flex gap-4 items-start">
                        <AlertTriangle className="w-5 h-5 text-piggy-pink-300 flex-shrink-0 mt-0.5" />
                        <div className="text-xs leading-relaxed text-piggy-cream/70 space-y-2">
                            <p>
                                <span className="font-semibold text-piggy-cream">Disclaimer.</span>{" "}
                                Safe Piggy ($PIGGY) is an experimental, non-custodial protocol on
                                Robinhood Chain. Cryptocurrency, DeFi tokens, NFTs, and reward
                                mechanisms involve substantial risk of total loss — including but
                                not limited to smart contract vulnerabilities, market volatility,
                                impermanent loss, regulatory uncertainty, and the possibility that
                                liquidity, tax revenue, or rewards may decline to zero. Past or
                                projected returns are not indicative of future results, and no
                                outcome is promised, implied, or guaranteed by the developers or
                                community.
                            </p>
                            <p>
                                Nothing on this site is financial, legal, tax, or investment
                                advice. You are solely responsible for your own decisions and for
                                complying with the laws of your jurisdiction. Do not participate
                                if doing so would violate local regulations. The protocol is{" "}
                                <span className="font-semibold">not available to users in the
                                United States or any OFAC-sanctioned jurisdiction.</span>{" "}
                                By using this site you confirm you are not a restricted person
                                and that you have read, understood, and accepted all risks.
                            </p>
                            <p>
                                Smart contracts may contain bugs. They have undergone static
                                analysis and Foundry test coverage but have not received a paid
                                third-party audit at this time. Audit status will be published
                                publicly as it changes. Only deposit funds you can afford to lose
                                entirely.
                            </p>
                            <p>
                                Safe Piggy is an independent project deployed on Robinhood Chain.
                                Not affiliated with, endorsed, or sponsored by Robinhood Markets,
                                Inc.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="border-t border-white/[0.04]">
                <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-piggy-cream/40">
                    <div>© {new Date().getFullYear()} Safe Piggy Protocol. Use at your own risk.</div>
                    <div className="flex gap-4">
                        {/* Terms/Privacy pages don't exist yet — don't ship dead legal
                            links. Add them back when the documents are real. */}
                        <a href="#disclaimer" className="hover:text-piggy-cream/70">Risk Disclosures</a>
                    </div>
                </div>
            </div>
        </footer>
    );
}

type FooterLink = { label: string; to?: string; href?: string };

function FooterCol({ title, items }: { title: string; items: FooterLink[] }) {
    const cls = "text-piggy-cream/70 text-sm hover:text-piggy-cream transition-colors";
    return (
        <div>
            <div className="text-piggy-cream/40 text-xs font-semibold uppercase tracking-wider mb-3">{title}</div>
            <ul className="space-y-2">
                {items.map((i) => (
                    <li key={i.label}>
                        {i.to ? (
                            <Link to={i.to} className={cls}>{i.label}</Link>
                        ) : (
                            <a href={i.href} target="_blank" rel="noopener noreferrer" className={cls}>
                                {i.label}
                            </a>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

